import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Modal, Row } from 'antd';
import DealershipChooser from 'components/DealershipChooser';
import PitstopSimpleTable from 'components/PitstopSimpleTable';
import LocationFormModal from 'components/ShopLocations/LocationFormModal';
import { get } from 'lodash';
import { makeObservable, observable, observe } from 'mobx';
import { observer } from 'mobx-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import { withRouter } from 'react-router';
import { PitstopButton } from 'shared';
import { ShopStore } from 'stores';
import AppStore from 'stores/App';
import { Logger } from 'stores/Classes';
import styled from 'styled-components';

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  background-color: #ffffff;
  padding: 25px 0 10px;
`;

const ActionButtonsWrapper = styled.div`
  right: 15px;

  button {
    margin-left: 10px;
  }
`;

class ShopLocationsPage extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    match: PropTypes.object,
  };

  shopId = ShopStore.currentShop.id;
  LOCATION_FORM_MODAL_ID = 'location_form_modal_id';

  state = {
    savingLocation: false,
    loading: false,
    data: [],
    locationModalTitle: 'Add Sub-location',
    selectedLocationData: {},
    selectedLocations: [],
  };

  columns = [
    {
      title: 'Sub-location',
      dataIndex: 'code',
      key: 'code',
      render: (text, record) => (
        <span
          onClick={() => this.onClickEdit(record)}
          style={{ cursor: 'pointer' }}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => {
        return (
          <>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => this.onClickEdit(record)}
            >
              Edit
            </Button>
            {/* <Button onClick={() => this.onDeleteLocation(record)}>Delete</Button> */}
          </>
        );
      },
    },
  ];

  disposer = observe(ShopStore.currentShop, 'id', async () => {
    return this.refreshLocations(ShopStore.currentShop.id);
  });

  componentWillUnmount() {
    this.disposer();
  }

  componentDidMount() {
    AppStore.setSelectedSidebarKey('/shop-locations');
    this.refreshLocations();
  }

  onClickAddLocation = () => {
    this.setState(
      {
        locationModalTitle: 'Add Sub-location',
        selectedLocationData: {},
      },
      () => {
        AppStore.openModals.set(this.LOCATION_FORM_MODAL_ID, true);
      }
    );
  };

  onClickEdit = async (location) => {
    this.setState(
      {
        locationModalTitle: 'Edit Sub-location',
        selectedLocationData: location,
      },
      () => {
        AppStore.openModals.set(this.LOCATION_FORM_MODAL_ID, true);
      }
    );
  };

  refreshLocations = async (shopId = ShopStore.currentShop.id) => {
    try {
      await this.setState({ loading: true });
      // locations are saved in shop settings for now
      const shopSettings = await ShopStore.getShopSettings(shopId);
      const locations = get(shopSettings, 'result[0].settings.locations', []);
      await this.setState({ data: locations });
    } catch (error) {
      Logger.log(error);
      AppStore.addError('Error loading sub-locations: ' + error.message);
    } finally {
      this.setState({ loading: false });
    }
  };

  onSubmitLocationForm = async (data) => {
    try {
      await this.setState({ savingLocation: true });
      // locations are saved in shop settings for now
      const shopSettings = await ShopStore.getShopSettings(
        ShopStore.currentShop.id
      );
      const locations = get(shopSettings, 'result[0].settings.locations', []);
      // if id is present, update the existing Location
      const existingLocation = locations.find(
        (location) => location.id === data.id
      );
      let updatedLocations;
      if (existingLocation) {
        // update the existing Location
        updatedLocations = locations.map((location) =>
          location.id === data.id ? data : location
        );
      } else {
        // otherwise, add a new Location
        updatedLocations = [
          ...locations,
          {
            id: data.id || moment().unix(),
            code: data.code,
            description: data.description,
          },
        ];
      }
      await ShopStore.upsertShopSettings(ShopStore.currentShop.id, {
        ...get(shopSettings, 'result[0].settings', {}),
        locations: updatedLocations,
      });
      AppStore.addSuccess('Location saved successfully');
      await this.refreshLocations();
      this.setState({ selectedLocationData: {} });
      AppStore.openModals.set(this.LOCATION_FORM_MODAL_ID, false);
    } catch (error) {
      console.log(error);
      AppStore.addError('Error saving location: ' + error.message);
    } finally {
      await this.setState({ savingLocation: false });
    }
  };

  onDeleteLocations = async () => {
    try {
      await this.setState({ loading: true });
      const shopSettings = await ShopStore.getShopSettings(
        ShopStore.currentShop.id
      );
      const locations = get(shopSettings, 'result[0].settings.locations', []);
      const selectedLocationIds = this.state.selectedLocations.map(
        (sl) => sl.id
      );
      const updatedLocations = locations.filter(
        (l) => !selectedLocationIds.includes(l.id)
      );
      await ShopStore.upsertShopSettings(ShopStore.currentShop.id, {
        ...get(shopSettings, 'result[0].settings', {}),
        locations: updatedLocations,
      });
      await this.refreshLocations();
      this.setState({ selectedLocations: [] });
      AppStore.addSuccess('Locations deleted successfully');
    } catch (error) {
      Logger.log(error);
      AppStore.addError('Error deleting location: ' + error.message);
    } finally {
      await this.setState({ loading: false });
    }
  };

  render() {
    return (
      <Fragment>
        <LocationFormModal
          modalId={this.LOCATION_FORM_MODAL_ID}
          saving={this.state.savingLocation}
          title={this.state.locationModalTitle}
          defaultData={this.state.selectedLocationData}
          onCancel={() => {
            this.setState({ selectedLocationData: {} });
            AppStore.openModals.set(this.LOCATION_FORM_MODAL_ID, false);
          }}
          onSubmit={this.onSubmitLocationForm}
        />

        <DealershipChooser />
        <ActionsWrapper>
          <h1>Sub-location Management</h1>
          <ActionButtonsWrapper>
            <PitstopButton
              icon={<PlusOutlined />}
              onClick={this.onClickAddLocation}
            >
              Add Sub-location
            </PitstopButton>
          </ActionButtonsWrapper>
        </ActionsWrapper>

        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Button
              type="primary"
              onClick={() => {
                Modal.confirm({
                  title: 'Are you sure you want to delete these sub-locations?',
                  content: 'This action cannot be undone.',
                  okText: 'Yes, Delete',
                  okType: 'danger',
                  cancelText: 'Cancel',
                  onOk: () => {
                    this.onDeleteLocations();
                  },
                });
              }}
              disabled={this.state.selectedLocations.length === 0}
            >
              Remove ({this.state.selectedLocations.length}) Sub-locations
            </Button>
          </Col>
          <Col span={24}>
            <PitstopSimpleTable
              rowKey={(record) => record.id}
              loading={this.state.loading}
              columns={this.columns}
              data={this.state.data}
              rowSelection={{
                type: 'checkbox',
                onChange: (selectedRowKeys, selectedRows) => {
                  this.setState({ selectedLocations: selectedRows });
                },
                getCheckboxProps: (record) => ({
                  name: record.code,
                }),
              }}
              onRowClick={() => {}}
            />
          </Col>
        </Row>
      </Fragment>
    );
  }
  constructor() {
    super();
    makeObservable(this, {
      shopId: observable,
    });
  }
}

export default withRouter(observer(ShopLocationsPage));
